/** Types for the secondary-connectivity review queue + manual overrides (Phase 3
 *  Stage 4). Mirror of the Stage-2 schemas in
 *  src/topology_platform/schemas/connectivity.py — keep in sync.
 *
 *  CONSUMPTION CONTRACT (Stage 2 handoff): every GET degrades to an empty page
 *  (total 0) / available:false rather than 4xx when no published run exists, so
 *  the review UI renders an "empty / not computed" state without try/catch. The
 *  derived tables are EMPTY until a gated prod compute runs. */

import type { ConnectivityTier, RootMethod } from './secondary.ts'

/** Why a component landed in the review bucket (backend `classify_why_flagged`). */
export type WhyFlagged =
  | 'unrooted'
  | 'low_confidence'
  | 'majority_disagrees_nearest'

/** Where a candidate transformer came from, so a reviewer can judge it. */
export type CandidateSource = 'derived_root' | 'meter_majority' | 'nearest_geom'

/** A transformer the review queue offers as a re-root candidate. */
export interface CandidateTransformer {
  transformer_id: number
  transformer_name: string | null
  source: CandidateSource | string
  /** Meters declaring this transformer (only meaningful for meter_majority). */
  meter_count: number | null
}

/** One ambiguous secondary component needing a human decision. `component_id` is
 *  meaningful ONLY within `run_id` (composite key) — never assume cross-run
 *  stability. Overrides are written at the component level (source_id =
 *  component_id) since only `component` source takes read-time effect today.
 *  `anchor_segment_id` is the import-stable cross-run re-apply key (a
 *  representative secondary_conductors.id) the UI echoes back on accept/reassign
 *  so the override survives a recompute that renumbers component_ids (P1-a). */
export interface ReviewQueueItem {
  run_id: number
  component_id: number
  feeder_id: number | null
  feeder_name: string | null
  anchor_segment_id: number | null
  transformer_id: number | null
  root_method: RootMethod | null
  root_confidence: number | null
  tier: ConnectivityTier
  transformer_reachable: boolean | null
  meter_count: number
  majority_share: number | null
  disagree_count: number
  why_flagged: WhyFlagged | string
  candidates: CandidateTransformer[]
}

/** A page of the review queue plus the total matching count (for paging). */
export interface ReviewQueueResponse {
  items: ReviewQueueItem[]
  total: number
  skip: number
  limit: number
}

/** Override source vocab. `component` is the only kind that takes read-time
 *  effect today; `meter`/`secondary_segment` are accepted+stored but inert. The
 *  review UI always overrides at the component level. */
export type OverrideSourceType = 'component' | 'meter' | 'secondary_segment'
export type OverrideTargetType = 'transformer'

/** Request body to POST /secondary/overrides. `created_by` is NOT sent — the
 *  server stamps the authed username. `target_type` defaults to 'transformer'.
 *  `feeder_id` + `anchor_segment_id` (echoed from the review row) scope the
 *  override and give it a stable cross-run re-apply key (P1-a) — always send a
 *  real `anchor_segment_id` when the row carries one. */
export interface OverrideCreate {
  source_type: OverrideSourceType
  source_id: number
  target_type?: OverrideTargetType
  target_id: number
  feeder_id?: number | null
  anchor_segment_id?: number | null
  method?: string
  reason?: string | null
}

/** A stored manual connectivity override (mirrors the model row). */
export interface OverrideResponse {
  id: number
  source_type: string
  source_id: number
  target_type: string
  target_id: number
  feeder_id: number | null
  anchor_segment_id: number | null
  method: string
  reason: string | null
  created_by: string | null
  created_at: string
  is_active: boolean
}

/** Human-readable label + dot color for a `why_flagged` reason. */
export const WHY_FLAGGED_LABELS: Record<string, { label: string; dot: string }> = {
  unrooted: { label: 'Unrooted', dot: 'bg-red-500' },
  low_confidence: { label: 'Low confidence', dot: 'bg-amber-500' },
  majority_disagrees_nearest: {
    label: 'Majority disagrees',
    dot: 'bg-purple-500',
  },
}

/** Body of POST /secondary/components/accept (Phase 5.1). Everything but the
 *  transformer is echoed straight from the review row. */
export interface ComponentAcceptRequest {
  run_id: number
  feeder_id: number
  component_id: number
  anchor_segment_id: number | null
  root_transformer_id: number
  reason?: string | null
}

export interface ComponentAcceptResponse {
  assigned: number
  unchanged: number
  conflicts: number[]
  override_id: number | null
}
